import { useMutation } from '@apollo/client';
import ConfirmationModal from '@components/modals/confirmationModal';
import type { RelatedEntity, RelatedEntityInput, User } from '@interfaces/user';
import { usePermission } from '@providers/userContextProvider';
import { generateClientNameString } from '@utils/generateClientNameString';
import { useSnackbar } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import {
  FETCH_USER_RELATED_ENTITIES,
  UPDATE_USER_RELATED_ENTITIES,
} from './relatedEntitiesQueries';

export const RelatedEntityRemoveDialog = ({
  entityToRemove,
  existingRelatedEntities,
  user,
  onClose,
}: {
  entityToRemove: User;
  existingRelatedEntities: RelatedEntity[];
  user: User;
  onClose: () => void;
}) => {
  const { t } = useTranslation('nonIndividualClients');
  const { enqueueSnackbar } = useSnackbar();
  const canReadClientLowRiskPii = usePermission('read:client_low_risk_pii');

  const [updateUserRelatedEntities, { loading: deletePending }] = useMutation(
    UPDATE_USER_RELATED_ENTITIES,
    {
      refetchQueries: [
        FETCH_USER_RELATED_ENTITIES({ canReadClientLowRiskPii }),
      ],
    },
  );

  const deleteMember = () => {
    const updatedRelatedEntities: RelatedEntityInput[] = existingRelatedEntities
      .filter((relEnt: RelatedEntity) => relEnt.entity.id !== entityToRemove.id)
      .map((relEnt: RelatedEntity) => ({
        entityId: relEnt.entity.id,
        relation: relEnt.relation,
      }));

    updateUserRelatedEntities({
      variables: {
        userId: user.id,
        relatedEntities: updatedRelatedEntities,
      },
      refetchQueries: [
        FETCH_USER_RELATED_ENTITIES({ canReadClientLowRiskPii }),
      ],
      onCompleted: () => {
        enqueueSnackbar(t('remove.hasBeenRemoved'), { variant: 'success' });
        onClose();
      },
    });
  };

  return (
    <ConfirmationModal
      open={true}
      title={t('remove.areYouSure', {
        removedWhoName: generateClientNameString(entityToRemove),
      })}
      bodyText={t('remove.thisWillNotDelete', {
        removedWhoName: generateClientNameString(entityToRemove),
        removedFromName: generateClientNameString(user),
      })}
      onConfirm={deleteMember}
      onCancel={onClose}
      confirmButton={t('remove.yesRemove')}
      cancelButton={t('shared:cancel')}
      loading={deletePending}
      confirmColor='error'
    />
  );
};
